import ElevatedButton from 'components/ElevatedButton';
import EmotionRadio from 'containers/CompositionFeedback/EmotionRadio';
import {useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {createUserFeelingsRequest} from 'store/reducers/userFeelings';
import {DevolutiveStage, Emotion} from 'store/services/userFeelings';
import {useMobile} from 'utils/hooks';
import styles from './FeelingsView.module.scss';

export interface FeelingsViewProps {
  userFeelingsStage: DevolutiveStage;
  createUserFeelingsRequest: typeof createUserFeelingsRequest;
  onFinish?(): void;
}

function FeelingsView({
  userFeelingsStage,
  createUserFeelingsRequest,
  onFinish,
}: FeelingsViewProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const {composition_id} = useParams();
  const {isMobile} = useMobile();

  const [formDataGlobal, setFormDataGlobal] = useState<FormData>();

  enum ButtonTexts {
    'pre-composition review' = 'Pular',
    'during-composition review' = 'Voltar',
  }

  function handleFeelingsFormFinish(formData?: FormData) {
    createUserFeelingsRequest({
      composition: Number(composition_id),
      choice: (formData?.get('emotion') as Emotion) ?? 'skipped',
      comment: formData?.get('feelings') as string,
      stage: userFeelingsStage,
    });

    if (onFinish) {
      onFinish();
    } else {
      navigate({
        pathname: `/atividades/escrita/${composition_id}/devolutiva/central`,
        search: location.search,
      });
    }
  }

  function handleSkip(stage?: DevolutiveStage) {
    if (stage !== 'during-composition review') {
      handleFeelingsFormFinish();
    }

    if (onFinish) {
      onFinish();
    } else {
      navigate({
        pathname: `/atividades/escrita/${composition_id}/devolutiva/central`,
        search: location.search,
      });
    }
  }

  return (
    <section
      className={`${styles.wrapper} ${
        onFinish ? styles.wrapperNewReviewed : ''
      }`}
    >
      <h1>Como você está se sentindo?</h1>
      <form
        id="feelingsForm"
        className={styles.feelingsForm}
        onSubmit={(event) => {
          handleFeelingsFormFinish(new FormData(event.currentTarget));
          event.preventDefault();
        }}
        onChange={(event) => {
          setFormDataGlobal(new FormData(event.currentTarget));
        }}
      >
        <fieldset className={styles.emotionsInput} aria-label="Emoções">
          <EmotionRadio name="emotion" emotion="neutral" value="neutral" />
          <EmotionRadio name="emotion" emotion="excited" value="excited" />
          <EmotionRadio name="emotion" emotion="nervous" value="nervous" />
          <EmotionRadio name="emotion" emotion="tired" value="tired" />
          <EmotionRadio name="emotion" emotion="content" value="satisfied" />
          <EmotionRadio name="emotion" emotion="happy" value="happy" />
        </fieldset>
        <label htmlFor="feelings">Por que você está se sentindo assim?</label>
        <textarea
          maxLength={1800}
          id="feelings"
          name="feelings"
          placeholder="Digite aqui..."
          disabled={!formDataGlobal?.get('emotion')}
        />
      </form>

      <footer className={styles.footer}>
        <ElevatedButton
          text="Responder"
          variant="primary"
          type="submit"
          form="feelingsForm"
          disabled={!formDataGlobal?.get('emotion')}
        />
        {isMobile ? (
          <button
            type="button"
            className={styles.skipButton}
            onClick={() => {
              handleSkip(userFeelingsStage);
            }}
          >
            {ButtonTexts[userFeelingsStage]}
          </button>
        ) : (
          <ElevatedButton
            text={onFinish ? 'Sair' : ButtonTexts[userFeelingsStage]}
            variant="secondary"
            onClick={() => {
              handleSkip(userFeelingsStage);
            }}
          />
        )}
      </footer>
    </section>
  );
}

export default FeelingsView;
